import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';

import { Messages } from '~src/codegen/Localize';
import React = require('react');
import { LoginUrlsWrapper } from '~src/authentication/components/loginSpace/LoginUrls';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ValidationHelpers } from '~src/userManagement/helpers/ValidationHelpers';

import { LocalAuthConfiguration } from '../models/LocalAuthConfiguration';
import { IPasswordPolicy } from '../models/validation/IPasswordPolicy';
import { AuthenticationService } from '../services/AuthenticationService';
import { LoginPageStyles } from '../styles/LoginPageStyles';

export interface TokenProps {
    token: string;
}

interface TokenRequestFormFields {
    newPassword: string;
    confirmPassword: string;
}

const buttonStyle: React.CSSProperties = {
    marginTop: CommonStyles.Sizes.XLargeSpace - CommonStyles.Sizes.DefaultSpace,
};

export function ResetPasswordWithTokenForm(props: TokenProps) {

    const [localAuthConfiguration, setLocalAuthConfiguration] = React.useState(LocalAuthConfiguration.default());

    React.useEffect(() => {
        LocalAuthConfiguration.fetchCurrentLocalAuthConfiguration()
            .then(serverConfig => setLocalAuthConfiguration(serverConfig));
    }, []);

    function validateNewPassword(value: string): string | undefined {
        if (!value) {
            return Messages.enterYourPassword();
        }
    }

    function validateConfirmPassword(confirmPassword: string, values: TokenRequestFormFields): string {
        if (confirmPassword && values.newPassword !== confirmPassword) {
            return Messages.passwordsDoNotMatch();
        }
    }

    function handleFailure(errorCode: number) {
        LoginUrlsWrapper.navigateToSignIn({
            error: errorCode
        });
    }

    function sendPasswordResetRequest(formProps: TokenRequestFormFields, formik: FormikHelpers<any>): void {
        AuthenticationService.sendPasswordResetRequest('', '', formProps.newPassword, props.token)
            .then((resetPasswordResult: IResult) => {
                LoginUrlsWrapper.navigateToSignIn({
                    passwordResetSuccess: true
                });
            }, (errorCode: number) => {
                handleFailure(errorCode);
            });
    }

    function getFailedPasswordPolicies(newPassword: string): IPasswordPolicy[] {
        return localAuthConfiguration.performPasswordValidation(newPassword);
    }

    return (<Formik
        initialValues={{ newPassword: '', confirmPassword: '' }}
        onSubmit={(values: TokenRequestFormFields, formik: FormikHelpers<any>) => {
            sendPasswordResetRequest(values, formik);
        }}
        enableReinitialize={true}
    >
        {(formik: FormikProps<TokenRequestFormFields>) => (
            <form name='passwordResetForm' autoComplete='off' noValidate onSubmit={formik.handleSubmit} data-tb-test-id='passwordResetForm'>
                <div className='tb-login-text-box tb-padded'>
                    <FormikTextBox
                        name='newPassword'
                        required={true}
                        label={Messages.newPassword()}
                        type={'password'}
                        className={LoginPageStyles.textboxStyle}
                        validate={validateNewPassword}
                        spellCheck={false}
                        showValidationMessage={true}
                        eagerValidation={true}
                        testId='textbox-password'
                        onClear={() => formik.setFieldValue('newPassword', '')} />
                </div>
                <div className='tb-login-text-box tb-padded'>
                    <FormikTextBox
                        name='confirmPassword'
                        required={true}
                        label={Messages.confirmPassword()}
                        type={'password'}
                        className={LoginPageStyles.textboxStyle}
                        validate={
                            (value: string) => validateConfirmPassword(value, formik.values)
                        }
                        spellCheck={false}
                        showValidationMessage={true}
                        eagerValidation={true}
                        testId='textbox-confirm'
                        onClear={() => formik.setFieldValue('confirmPassword', '')} />
                </div>
                <div className={'tb-login-msg-container'}>
                    <div className={'tb-login-msg tb-login-error'} tb-test-id="message-failure" role="alert">
                        {formik.values.newPassword && ValidationHelpers.createPasswordPolicyErrorMessageSectionFor(getFailedPasswordPolicies(formik.values.newPassword))}
                    </div>
                </div>
                <ButtonWidget buttonType={ButtonType.Outline}
                    type='submit'
                    style={buttonStyle}
                    tb-test-id="button-reset"
                    testId='btn-reset'
                    disabled={!formik.values.newPassword || !formik.values.confirmPassword || !formik.isValid || getFailedPasswordPolicies(formik.values.newPassword).length > 0 || formik.values.newPassword !== formik.values.confirmPassword}
                    className={LoginPageStyles.submitButtonStyle}>
                    {Messages.resetPassword()}
                </ButtonWidget>
            </form>
        )}
    </Formik>);
}